import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Modal, TextInput, Text } from '@good/ui/core';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { IRootDispatch } from 'stores/rematch/root-store';
import TermsConditionsEditor, {
  TermsConditionsEditorRef,
} from 'views/customers/details/tabs-panel/service-agreement/TermsConditionsEditor';
import { StaticValue } from 'views/sil-service/components/inline-form/static-value';
import { notification } from 'antd';

interface ServiceAgreementTemplate {
  termsConditionsTemplateId: string;
  name: string;
  data: string;
}

interface IServiceAgreementTemplateModalProps {
  onClose: () => void;
  isOpen: boolean;
  template: ServiceAgreementTemplate | null;
  displayMode: string;
  existingNames: Array<string>;
}

const createTemplateSchema = (existingNames: Array<string>, currentTemplateName?: string) =>
  z.object({
    name: z
      .string()
      .min(1, 'Enter a name.')
      .max(50, 'Please enter less than 50 characters.')
      .refine((name) => {
        const trimmedName = name.trim();
        // If editing, exclude current template name from validation
        const namesToCheck = currentTemplateName
          ? existingNames.filter((name) => name !== currentTemplateName)
          : existingNames;
        return !namesToCheck.includes(trimmedName);
      }, 'Template name already exists.'),
  });

const ServiceAgreementTemplateModal: React.FC<IServiceAgreementTemplateModalProps> = ({
  onClose,
  isOpen,
  template,
  displayMode,
  existingNames,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const editorRef = useRef<TermsConditionsEditorRef>(null);
  const dispatch = useDispatch<IRootDispatch>();

  const action = displayMode === 'CREATE' ? 'Create' : displayMode === 'EDIT' ? 'Edit' : 'View';

  const title = `${action} Service Agreement Terms & Conditions`;

  const schema = createTemplateSchema(existingNames, template?.name);
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: template?.name || '',
    },
  });

  // Reset form when template changes
  useEffect(() => {
    form.reset({
      name: template?.name || '',
    });
  }, [template, form]);

  const handleSave = useCallback(async () => {
    setIsLoading(true);

    try {
      const termsValidationResult = editorRef.current?.validate();

      // Validate terms and conditions content
      if (!termsValidationResult?.isValid || !termsValidationResult?.content) {
        notification.error({ message: 'Please enter terms and conditions.' });
        setIsLoading(false);
        return;
      }

      if (displayMode === 'CREATE') {
        const isValid = await form.trigger();
        if (!isValid) {
          notification.error({ message: 'Please fix the form errors before saving.' });
          setIsLoading(false);
          return;
        }

        const formData = form.getValues();

        await dispatch.accountStore.doCreateServiceAgreementTemplates({
          name: formData.name.trim(),
          data: termsValidationResult.content,
          paymentSourceType: 'NDIS',
        });
        onClose();
        setIsLoading(false);
      } else if (displayMode === 'EDIT' && template) {
        const isValid = await form.trigger();
        if (!isValid) {
          notification.error({ message: 'Please fix the form errors before saving.' });
          setIsLoading(false);
          return;
        }

        const formData = form.getValues();

        await dispatch.accountStore.doUpdateServiceAgreementTemplates({
          templateId: template.termsConditionsTemplateId,
          name: formData.name.trim(),
          data: termsValidationResult.content,
          paymentSourceType: 'NDIS',
        });
        onClose();
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    } catch (e) {
      console.error('Error saving service agreement template:', e);
      console.error('Error details:', {
        message: e.message,
        stack: e.stack,
        response: e.response,
        data: e.data,
      });
      notification.error({ message: 'Oops, something went wrong! Please try again.' });
      setIsLoading(false);
    }
  }, [displayMode, template, form, onClose, dispatch]);

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      size='lg'
      title={title}
      maxHeight='90vh'
      confirmProps={{
        children: displayMode === 'VIEW' ? 'Close' : 'Save',
        onClick: displayMode === 'VIEW' ? onClose : handleSave,
        loading: isLoading,
      }}
      cancelProps={{
        children: 'Cancel',
        onClick: onClose,
        style: { display: displayMode === 'VIEW' ? 'none' : 'inline-block' },
      }}
    >
      <div className='space-y-4'>
        {displayMode === 'VIEW' ? (
          <div className='space-y-2'>
            <Text size='sm' className='font-semibold text-gray-600'>
              Template Name
            </Text>
            <StaticValue value={template?.name || ''} emptyValue='No template name set.' />
          </div>
        ) : (
          <div className='space-y-2'>
            <Text size='sm' className='font-semibold text-gray-600'>
              Template Name
            </Text>
            <TextInput
              {...form.register('name')}
              size='md'
              maxLength={50}
              placeholder='Enter name...'
              error={form.formState.errors.name?.message}
              style={{ width: '540px' }}
            />
          </div>
        )}

        {displayMode === 'VIEW' ? (
          <div className='space-y-2'>
            <Text size='sm' className='font-semibold text-gray-600'>
              Terms and Conditions
            </Text>
            <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
              <StaticValue value={template?.data || ''} html={true} emptyValue='No terms and conditions set.' />
            </div>
          </div>
        ) : (
          <div className='space-y-2'>
            <Text size='sm' className='font-semibold text-gray-600'>
              Terms and Conditions
            </Text>
            <TermsConditionsEditor ref={editorRef} contentHtml={template?.data || ''} minHeight={'200px'} />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ServiceAgreementTemplateModal;
